import { isValidHandoffCodeShape } from '@/features/auth/lib/handoff-code'
import {
  IdentityAuthPath,
  POST_LOGOUT_SKIP_AUTH_APEX_REDIRECT,
  clearTenantHandoffFailedFlag,
  setTenantHandoffFailedFlag,
} from '@/features/auth/lib/constants'
import { clearTenantHandoffStarted } from '@/features/auth/lib/tenant-handoff'

/** Bound handoff so a down `/api/v1/auth/handoff/use` proxy cannot block SPA mount forever. */
export const TENANT_HANDOFF_FETCH_TIMEOUT_MS = 20_000

/** Retries while the browser commits Set-Cookie from `/handoff/use` before SPA `/me`. */
const HANDOFF_SESSION_VERIFY_ATTEMPTS = 3
const HANDOFF_SESSION_VERIFY_GAP_MS = 40

function handoffAbortSignal(): AbortSignal {
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(TENANT_HANDOFF_FETCH_TIMEOUT_MS)
  }
  const c = new AbortController()
  globalThis.setTimeout(() => c.abort(), TENANT_HANDOFF_FETCH_TIMEOUT_MS)
  return c.signal
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    globalThis.setTimeout(resolve, ms)
  })
}

/**
 * Confirm the handoff Set-Cookie jar is visible to same-origin requests before
 * React mounts and RequireAuth fires `/me`. Without this, a racing 401 on the
 * tenant host used to bounce to `?auth_reset=1` and wipe the apex login cookies.
 */
async function verifyHandoffSession(): Promise<boolean> {
  for (let attempt = 0; attempt < HANDOFF_SESSION_VERIFY_ATTEMPTS; attempt += 1) {
    if (attempt > 0) {
      await delay(HANDOFF_SESSION_VERIFY_GAP_MS)
    } else {
      // Macrotask so the browser can commit Set-Cookie from the prior response.
      await delay(0)
    }
    try {
      const meUrl = new URL(IdentityAuthPath.ME, globalThis.location.origin)
      const meRes = await fetch(meUrl, {
        method: 'GET',
        credentials: 'include',
        signal: handoffAbortSignal(),
      })
      if (meRes.ok) {
        return true
      }
    } catch {
      // retry
    }
  }
  return false
}

/**
 * Clear tenant-origin logout/handoff guards that would otherwise block a fresh
 * `_handoff` exchange. sessionStorage is per-host: logging out on
 * `uuu.localhost` leaves `bf_post_logout_skip_auth_apex` on that origin, and a
 * later login→handoff would skip `/handoff/use` entirely (no cookies → soft
 * bounce back to apex). An inbound `_handoff` is an explicit new session.
 */
function clearTenantOriginGuardsForHandoff(): void {
  try {
    globalThis.sessionStorage?.removeItem(POST_LOGOUT_SKIP_AUTH_APEX_REDIRECT)
  } catch {
    // ignore
  }
  clearTenantHandoffFailedFlag()
}

/**
 * If the URL carries `_handoff`, exchange it for session cookies before the app tree mounts.
 * Always strips `_handoff` from the address bar when present (even on failure).
 */
export async function consumeTenantHandoffFromLocation(): Promise<void> {
  const params = new URLSearchParams(globalThis.location.search)
  const handoffCode = params.get('_handoff')
  if (!handoffCode) return

  if (isValidHandoffCodeShape(handoffCode)) {
    clearTenantOriginGuardsForHandoff()
    try {
      const handoffUrl = new URL(
        '/api/v1/auth/handoff/use',
        globalThis.location.origin,
      )
      handoffUrl.searchParams.set('code', handoffCode)
      const response = await fetch(handoffUrl, {
        method: 'POST',
        credentials: 'include',
        signal: handoffAbortSignal(),
      })
      if (response.ok) {
        const sessionOk = await verifyHandoffSession()
        if (sessionOk) {
          clearTenantHandoffStarted()
        } else {
          // Leave TENANT_HANDOFF_STARTED_AT so RequireAuth soft-bounces to apex
          // without auth_reset, preserving the original login cookies.
          setTenantHandoffFailedFlag()
        }
      } else {
        setTenantHandoffFailedFlag()
      }
    } catch {
      setTenantHandoffFailedFlag()
    }
  }

  params.delete('_handoff')
  const cleanPath =
    globalThis.location.pathname +
    (params.size > 0 ? `?${params.toString()}` : '')
  globalThis.history.replaceState(null, '', cleanPath)
}
